import React, { FC, useMemo, useRef } from "react";
import { ScrollView } from "react-native";
import {
  SPACE,
  Box,
  View,
  useDevice,
  Banner,
  BANNER_TYPE,
  ALIGN,
  FLEX_DIRECTION,
  COLOR,
  Layout as AuroraLayout,
} from "@lookiero/aurora";
import { useI18nMessage } from "@lookiero/i18n-react";
import { Button, Layout as UiLayout, Text } from "@lookiero/sty-psp-ui";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { RafComponent } from "../../../Root";
import { Shape } from "../../../components/shape/Shape";
import { useStaticInfo } from "../../../hooks/useStaticInfo";
import { DOMAIN, I18nMessages } from "../../../i18n/i18n";
import { CheckoutConfirmationHeader } from "../header/checkoutConfirmationHeader/CheckoutConfirmationHeader";
import { Layout } from "../layout/Layout";
import { style as checkoutConfirmationStyle } from "./CheckoutConfirmation.styles";

interface CheckoutConfirmationProps {
  readonly layout: UiLayout;
  readonly orderReference: string;
  readonly rafComponent: RafComponent;
  readonly onPressBanner: () => void;
  readonly onPressReturnToShop: () => void;
  readonly onClose: () => void;
  readonly onPressGoToOrder: () => void;
}

const CheckoutConfirmation: FC<CheckoutConfirmationProps> = ({
  layout,
  orderReference,
  rafComponent: RafComponent,
  onPressBanner,
  onPressReturnToShop,
  onClose,
  onPressGoToOrder,
}) => {
  const {
    cdnPath,
    tradename,
    session: {
      user: { segment },
    },
  } = useStaticInfo();

  const { screen } = useDevice();
  const isDesktopScreen = screen.L;

  const scrollRef = useRef<ScrollView>(null);

  const style = useMemo(() => checkoutConfirmationStyle(), []);

  const thankYouTitleText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.THANK_YOU_TITLE,
  });
  const thankYouBodyText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.THANK_YOU_BODY,
    values: {
      order_reference: (
        <Text style={style.textBold} selectable>
          {orderReference}
        </Text>
      ),
    },
  });
  const checkoutConfirmationExtraInfoText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.CHECKOUT_CONFIRMATION_EXTRA_INFO,
  });
  const checkoutConfirmationGoToOrderText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.CHECKOUT_CONFIRMATION_GO_TO_ORDER,
  });
  const emptyStateCtaText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.EMPTY_STATE_CTA,
  });
  const bannerLookAndLikeDescriptionText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.BANNER_LOOK_AND_LIKE_DESCRIPTION,
  });
  const bannerLookAndLikeTitleText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.BANNER_LOOK_AND_LIKE_TITLE,
  });
  const bannerLookAndLikeCtaText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.BANNER_LOOK_AND_LIKE_CTA,
  });

  const bannerImageUrl = useMemo(
    () => `${cdnPath}/images/shop/banners/look-and-like/${segment}.webp`,
    [cdnPath, segment],
  );

  return (
    <Layout
      footer={null}
      header={<CheckoutConfirmationHeader onClose={onClose} />}
      layout={layout}
      scrollRef={scrollRef}
    >
      <View style={style.wrapper} wide>
        <View style={style.contentWrapper}>
          {tradename === Tradename.LOOKIERO && <Shape type="doubleHalf" />}
          <Box
            size={{ L: "1/3", M: "2/3" }}
            style={[style.content, isDesktopScreen ? style.contentLarge : style.contentSmall]}
          >
            <View testID="checkout-confirmation" wide>
              <Text level={screen.L ? 2 : 3} style={style.title} variant="heading">
                {thankYouTitleText}
              </Text>

              <Text style={style.text} selectable>
                {thankYouBodyText}
              </Text>
              <Text style={style.text}>{checkoutConfirmationExtraInfoText}</Text>

              <View style={style.buttonsWrapper}>
                <Button
                  size="small"
                  style={{ button: style.goToOrderButton }}
                  variant="secondary"
                  onPress={onPressGoToOrder}
                >
                  {checkoutConfirmationGoToOrderText}
                </Button>
                <Button size="small" onPress={onPressReturnToShop}>
                  {emptyStateCtaText}
                </Button>
              </View>
            </View>
          </Box>
          {tradename === Tradename.LOOKIERO && <Shape type="rectangle" />}
        </View>

        <AuroraLayout backgroundColor={COLOR.BG_PRIMARY_LIGHT} paddingTop={SPACE.SPACE_8}>
          <Box
            align={ALIGN.CENTER}
            flexDirection={isDesktopScreen ? FLEX_DIRECTION.ROW : FLEX_DIRECTION.COLUMN}
            size={{ L: "2/3" }}
          >
            <RafComponent bannerType={BANNER_TYPE.ROUNDED} style={{ flex: 1 }} />

            <View margin={isDesktopScreen ? SPACE.SPACE_3 : SPACE.SPACE_2} />

            <Banner
              description={bannerLookAndLikeDescriptionText}
              image={{ src: bannerImageUrl }}
              style={{ flex: 1 }}
              title={bannerLookAndLikeTitleText}
              type={BANNER_TYPE.ROUNDED}
              primaryButton={{
                text: bannerLookAndLikeCtaText,
                onPress: onPressBanner,
              }}
              scaled
            />
          </Box>
        </AuroraLayout>
      </View>
    </Layout>
  );
};

export { CheckoutConfirmation };
